/**
 * Port Mapping Store
 *
 * Manages port mapping templates and rules for survey cable generation.
 * Uses unified storage service for automatic persistence (fallback).
 * Supports IStorageAdapter for SQL storage (when injected).
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  PortMappingStrategy,
  type SurveyPortMapping,
  type PortMappingTemplate,
  type PortMappingRule,
  type PortMappingFileData,
  type PortConnection,
  type CablePortMapping,
} from '@/types/portMapping.types'
import * as UnifiedStorage from '@/services/unified-storage.service'
import { useStorageAdapter, type StoreOperationResult } from '@/composables/useStorageAdapter'
import { StorageErrorCode } from '@/services/storage/IStorageAdapter'
import type { IStorageAdapter } from '@/services/storage/IStorageAdapter'
import { reportError } from '@/lib/error-reporting'

/**
 * Save port mapping to unified storage (fallback)
 */
async function saveToStorage(surveyId: string, data: SurveyPortMapping): Promise<void> {
  try {
    await UnifiedStorage.updateSurveyPortMapping(surveyId, data)
  } catch (error) {
    reportError(error, { where: 'portMapping.store.saveToStorage', extra: { surveyId } })
  }
}

/**
 * Apply a port mapping strategy to generate connections
 */
function applyStrategy(
  strategy: PortMappingStrategy,
  sourcePortCount: number,
  destinationPortCount: number,
  customConnections?: PortConnection[]
): PortConnection[] {
  const connections: PortConnection[] = []

  switch (strategy) {
    case PortMappingStrategy.OneToOne: {
      // Map port 1->1, 2->2, etc. up to min of both counts
      const maxPorts = Math.min(sourcePortCount, destinationPortCount)
      for (let i = 1; i <= maxPorts; i++) {
        connections.push({ sourcePort: i, destinationPort: i })
      }
      break
    }

    case PortMappingStrategy.AllToFirst: {
      // Map all source ports to destination port 1
      for (let i = 1; i <= sourcePortCount; i++) {
        connections.push({ sourcePort: i, destinationPort: 1 })
      }
      break
    }

    case PortMappingStrategy.FirstToAll: {
      // Map source port 1 to all destination ports
      for (let i = 1; i <= destinationPortCount; i++) {
        connections.push({ sourcePort: 1, destinationPort: i })
      }
      break
    }

    case PortMappingStrategy.Custom: {
      // Use provided custom connections
      if (customConnections) {
        connections.push(...customConnections)
      }
      break
    }
  }

  return connections
}

export const usePortMappingStore = defineStore('portMapping', () => {
  // Storage adapter composable
  const storage = useStorageAdapter<SurveyPortMapping>()

  // State
  const portMappings = ref<Map<string, SurveyPortMapping>>(new Map())
  const activeSurveyId = ref<string | null>(UnifiedStorage.getActiveSurveyId())
  const isInitialized = ref(false)
  const isViewingSnapshot = ref(false) // Track when viewing historical version data

  // Default template factory
  const createDefaultTemplate = (): PortMappingTemplate => ({
    id: 'default',
    name: 'Default (1-to-1)',
    description: 'Maps port 1 to port 1, port 2 to port 2, etc.',
    rules: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  })

  // Create empty port mapping
  const createEmptyPortMapping = (surveyId: string): SurveyPortMapping => ({
    surveyId,
    activeTemplateId: 'default',
    templates: [createDefaultTemplate()],
  })

  // Computed
  const activePortMapping = computed(() => {
    if (!activeSurveyId.value) return null
    return portMappings.value.get(activeSurveyId.value) || null
  })

  const activeTemplateId = computed(() => activePortMapping.value?.activeTemplateId || null)

  const templates = computed(() => activePortMapping.value?.templates || [])

  const activeTemplate = computed(() => templates.value.find(t => t.id === activeTemplateId.value))

  const templateCount = computed(() => templates.value.length)

  const hasActiveTemplate = computed(() => activeTemplateId.value !== null)

  // Actions

  /**
   * Set storage adapter for SQL storage support
   */
  function setStorageAdapter(adapter: IStorageAdapter<SurveyPortMapping>): void {
    storage.setAdapter(adapter)
  }

  /**
   * Load port mapping from storage adapter
   */
  async function loadPortMapping(surveyId: string): Promise<boolean> {
    const adapter = storage.adapter()
    if (!adapter) {
      return true // No adapter, use fallback
    }

    const result = await storage.load(surveyId)

    if (!result.success) {
      return false
    }

    if (result.data) {
      portMappings.value.set(surveyId, result.data.data)
    } else {
      // Initialize empty port mapping if none exists
      const emptyMapping = createEmptyPortMapping(surveyId)
      portMappings.value.set(surveyId, emptyMapping)
    }

    return true
  }

  /**
   * Set the active survey
   */
  async function setActiveSurvey(surveyId: string): Promise<void> {
    activeSurveyId.value = surveyId

    // If viewing snapshot data, skip loading from storage to preserve restored state
    if (isViewingSnapshot.value && portMappings.value.has(surveyId)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[PortMappingStore] Viewing snapshot, skipping storage load')
      }
      return
    }

    // If we have a SQL adapter, use it directly
    const adapter = storage.adapter()
    if (adapter) {
      // Try to load from SQL storage adapter
      await loadPortMapping(surveyId)
      isInitialized.value = true
      return
    }

    // No SQL adapter - fall back to unified storage (localStorage)
    const surveyData = await UnifiedStorage.initializeSurvey({ surveyId, surveyName: surveyId })
    if (!portMappings.value.has(surveyId)) {
      portMappings.value.set(surveyId, surveyData.portMapping)
    }
    isInitialized.value = true
  }

  /**
   * Persist current state to storage
   */
  async function persist(): Promise<StoreOperationResult<SurveyPortMapping>> {
    if (!activeSurveyId.value) {
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'No active survey',
        },
      }
    }

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) {
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'Port mapping not found',
        },
      }
    }

    // If storage adapter is set, use it
    if (storage.adapter()) {
      return await storage.save(activeSurveyId.value, portMapping)
    }

    // Fallback to unified storage
    await saveToStorage(activeSurveyId.value, portMapping)
    return { success: true }
  }

  /**
   * Set the active template
   */
  async function setActiveTemplate(templateId: string): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    const template = portMapping.templates.find(t => t.id === templateId)
    if (template) {
      portMapping.activeTemplateId = templateId
      await persist()
    }
  }

  /**
   * Create a new template
   */
  async function createTemplate(
    name: string,
    description?: string
  ): Promise<PortMappingTemplate | null> {
    if (!activeSurveyId.value) return null

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return null

    const template: PortMappingTemplate = {
      id: `template-${Date.now()}`,
      name,
      ...(description !== undefined && { description }),
      rules: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    }
    portMapping.templates.push(template)
    await persist()
    return template
  }

  /**
   * Update a template
   */
  async function updateTemplate(
    templateId: string,
    updates: Partial<Pick<PortMappingTemplate, 'name' | 'description'>>
  ): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    const template = portMapping.templates.find(t => t.id === templateId)
    if (template) {
      if (updates.name !== undefined) template.name = updates.name
      if (updates.description !== undefined) template.description = updates.description
      template.updatedAt = new Date()
      await persist()
    }
  }

  /**
   * Delete a template
   */
  async function deleteTemplate(templateId: string): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    // Don't delete the default template
    if (templateId === 'default') return

    const index = portMapping.templates.findIndex(t => t.id === templateId)
    if (index !== -1) {
      portMapping.templates.splice(index, 1)
      // If we deleted the active template, switch to default
      if (portMapping.activeTemplateId === templateId) {
        portMapping.activeTemplateId = 'default'
      }
      await persist()
    }
  }

  /**
   * Add a rule to a template
   */
  async function addRule(
    templateId: string,
    sourceEquipmentId: string,
    destinationEquipmentId: string,
    strategy: PortMappingStrategy,
    connections?: PortConnection[],
    description?: string
  ): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    const template = portMapping.templates.find(t => t.id === templateId)
    if (template) {
      const rule: PortMappingRule = {
        id: `rule-${Date.now()}`,
        sourceEquipmentId,
        destinationEquipmentId,
        strategy,
        ...(connections !== undefined && { connections }),
        ...(description !== undefined && { description }),
      }
      template.rules.push(rule)
      template.updatedAt = new Date()
      await persist()
    }
  }

  /**
   * Update a rule in a template
   */
  async function updateRule(
    templateId: string,
    ruleId: string,
    updates: Partial<Omit<PortMappingRule, 'id'>>
  ): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    const template = portMapping.templates.find(t => t.id === templateId)
    if (template) {
      const rule = template.rules.find(r => r.id === ruleId)
      if (rule) {
        Object.assign(rule, updates)
        template.updatedAt = new Date()
        await persist()
      }
    }
  }

  /**
   * Delete a rule from a template
   */
  async function deleteRule(templateId: string, ruleId: string): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    const template = portMapping.templates.find(t => t.id === templateId)
    if (template) {
      const index = template.rules.findIndex(r => r.id === ruleId)
      if (index !== -1) {
        template.rules.splice(index, 1)
        template.updatedAt = new Date()
        await persist()
      }
    }
  }

  /**
   * Get port mapping for a cable connection
   */
  function getPortMapping(
    sourceEquipmentId: string,
    destinationEquipmentId: string,
    sourcePortCount: number,
    destinationPortCount: number,
    cableId?: string,
    sourceInstanceId?: string,
    destinationInstanceId?: string
  ): CablePortMapping | null {
    if (!activeTemplate.value) return null

    // Find matching rule
    const rule = activeTemplate.value.rules.find(
      r =>
        r.sourceEquipmentId === sourceEquipmentId &&
        r.destinationEquipmentId === destinationEquipmentId
    )

    // If no specific rule, use default one-to-one strategy
    const strategy: PortMappingStrategy = rule?.strategy || PortMappingStrategy.OneToOne
    const customConnections = rule?.connections

    const connections = applyStrategy(
      strategy,
      sourcePortCount,
      destinationPortCount,
      customConnections
    )

    return {
      cableId: cableId || 'unknown',
      sourceEquipmentInstanceId: sourceInstanceId || sourceEquipmentId,
      destinationEquipmentInstanceId: destinationInstanceId || destinationEquipmentId,
      connections,
      templateId: activeTemplate.value.id,
      ruleId: rule?.id || 'default',
    }
  }

  /**
   * Export templates to file data
   */
  function exportToFile(surveyName: string): PortMappingFileData {
    return {
      version: '1.0.0',
      surveyId: activeSurveyId.value || '',
      surveyName,
      exportedAt: new Date().toISOString(),
      templates: templates.value,
      activeTemplateId: activeTemplateId.value,
    }
  }

  /**
   * Import templates from file data
   */
  async function importFromFile(fileData: PortMappingFileData): Promise<void> {
    // Validate version
    if (fileData.version !== '1.0.0') {
      throw new Error(`Unsupported file version: ${fileData.version}`)
    }

    // Import templates (merge with existing)
    for (const importedTemplate of fileData.templates) {
      // Check if template already exists
      const existingIndex = templates.value.findIndex(t => t.id === importedTemplate.id)
      if (existingIndex !== -1) {
        // Update existing template - safe because we validated the index
        // eslint-disable-next-line security/detect-object-injection
        templates.value[existingIndex] = importedTemplate
      } else {
        // Add new template
        templates.value.push(importedTemplate)
      }
    }

    // Set active template if specified
    if (fileData.activeTemplateId) {
      const imported = templates.value.find(t => t.id === fileData.activeTemplateId)
      if (imported && activePortMapping.value) {
        activePortMapping.value.activeTemplateId = fileData.activeTemplateId
      }
    }

    await persist()
  }

  /**
   * Clear all port mappings
   * @param skipPersist - If true, only clears local state without persisting (used when data already deleted from SQL)
   */
  async function clear(skipPersist = false): Promise<void> {
    if (!activeSurveyId.value) return

    const portMapping = portMappings.value.get(activeSurveyId.value)
    if (!portMapping) return

    // Reset to default template only
    portMapping.templates = [createDefaultTemplate()]
    portMapping.activeTemplateId = 'default'
    if (!skipPersist) {
      await persist()
    }
  }

  /**
   * Get the complete state for a survey (for report snapshots)
   */
  function getStateForSurvey(surveyId: string): SurveyPortMapping | null {
    return portMappings.value.get(surveyId) || null
  }

  /**
   * Restore state for a survey from a snapshot
   */
  /**
   * Restore state for a survey from a snapshot
   * @param surveyId - The survey ID to restore state for
   * @param state - The state to restore
   * @param skipPersist - If true, only update in-memory state (for version switching)
   */
  async function restoreStateForSurvey(
    surveyId: string,
    state: SurveyPortMapping,
    skipPersist = false
  ): Promise<void> {
    if (!state || state.surveyId !== surveyId) {
      console.warn('Invalid port mapping state or surveyId mismatch')
      return
    }

    // Mark as viewing snapshot if skipping persist (i.e., loading historical version)
    if (skipPersist) {
      isViewingSnapshot.value = true
    }

    // Update in-memory state
    activeSurveyId.value = surveyId
    portMappings.value.set(surveyId, state)

    // Only persist if not restoring from snapshot (version switching)
    if (!skipPersist) {
      await persist()
    }
  }

  /**
   * Clear the snapshot viewing flag.
   * Call this when generating a new report or switching to the latest version
   * to allow loading from storage again.
   */
  function clearSnapshotMode(): void {
    isViewingSnapshot.value = false
  }

  return {
    // State
    activeSurveyId,
    activeTemplateId,
    templates,
    isInitialized,
    isViewingSnapshot,

    // Computed
    activeTemplate,
    templateCount,
    hasActiveTemplate,

    // Actions
    setStorageAdapter,
    setActiveSurvey,
    setActiveTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    addRule,
    updateRule,
    deleteRule,
    getPortMapping,
    exportToFile,
    importFromFile,
    clear,
    getStateForSurvey,
    restoreStateForSurvey,
    clearSnapshotMode,
  }
})
